import {
	createAnalyticsQueue,
	type EventProperties,
	FLUSH_AT,
	FLUSH_INTERVAL_MS,
	type QueuedEvent,
	type Session,
} from "./analytics-queue";
import { getDesktopBridge } from "./desktop";
import { env } from "./env";
import { trpcClient } from "./trpc";

const ANONYMOUS_ID_KEY = "pluck.analytics.anonymousId";
const SESSION_KEY = "pluck.analytics.session";
/** Lets a dev build send events (e.g. to test the admin dashboard). */
const DEV_ENABLED = import.meta.env.VITE_ANALYTICS_DEV === "true";

function newId(): string {
	try {
		return crypto.randomUUID();
	} catch {
		return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
	}
}

function readStorage(key: string): string | null {
	try {
		return localStorage.getItem(key);
	} catch {
		return null;
	}
}

function writeStorage(key: string, value: string): void {
	try {
		localStorage.setItem(key, value);
	} catch {
		// Storage unavailable (private mode); ids last for this page only.
	}
}

let anonymousId: string | null = null;
function getAnonymousId(): string {
	// Inside Pluck Desktop, share the main process's install id so renderer
	// and main-process events belong to the same visitor.
	const desktopId = getDesktopBridge()?.analyticsAnonymousId;
	if (desktopId) {
		return desktopId;
	}
	if (!anonymousId) {
		anonymousId = readStorage(ANONYMOUS_ID_KEY) ?? newId();
		writeStorage(ANONYMOUS_ID_KEY, anonymousId);
	}
	return anonymousId;
}

function loadSession(): Session | null {
	const raw = readStorage(SESSION_KEY);
	if (!raw) {
		return null;
	}
	try {
		const parsed = JSON.parse(raw) as Partial<Session>;
		return typeof parsed.id === "string" &&
			typeof parsed.lastActivity === "number"
			? { id: parsed.id, lastActivity: parsed.lastActivity }
			: null;
	} catch {
		return null;
	}
}

/** Fields every request carries. */
function envelope(sessionId: string, events: QueuedEvent[]) {
	const bridge = getDesktopBridge();
	return {
		source: bridge ? ("desktop" as const) : ("studio" as const),
		anonymousId: getAnonymousId(),
		sessionId: bridge?.analyticsSessionId ?? sessionId,
		appVersion: bridge?.version,
		platform: bridge?.platform ?? "web",
		events: events.map(({ name, at, properties, path }) => ({
			name,
			at,
			properties,
			path,
		})),
	};
}

const queue = createAnalyticsQueue({
	// Pluck Desktop owns the session (one per launch) when it provides one.
	externalSessionId: () => getDesktopBridge()?.analyticsSessionId,
	loadSession,
	saveSession: (session) => writeStorage(SESSION_KEY, JSON.stringify(session)),
	newId,
	now: () => Date.now(),
	onSessionStart: () => {
		queue.enqueue(
			"app.opened",
			{
				desktop: getDesktopBridge() !== null,
				screen_width: window.screen?.width,
			},
			currentPath()
		);
	},
	send: async (sessionId, events) => {
		await trpcClient.analytics.track.mutate(envelope(sessionId, events));
	},
});

let enabled = false;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleFlush(): void {
	if (flushTimer !== null) {
		return;
	}
	flushTimer = setTimeout(() => {
		flushTimer = null;
		queue.flush().catch(() => {
			/* best-effort */
		});
	}, FLUSH_INTERVAL_MS);
}

/**
 * The page is going away (or to the background): send what's queued with
 * keepalive fetches, which outlive the page, instead of the tRPC client.
 */
function flushOnExit(): void {
	if (flushTimer !== null) {
		clearTimeout(flushTimer);
		flushTimer = null;
	}
	for (const batch of queue.drain()) {
		const sessionId = batch[0]?.sessionId ?? "";
		fetch(`${env.VITE_SERVER_URL}/trpc/analytics.track`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(envelope(sessionId, batch)),
			credentials: "include",
			keepalive: true,
		}).catch(() => {
			/* best-effort */
		});
	}
}

/** The current location's path (hash routes in the desktop app included). */
function currentPath(): string {
	const { pathname, hash } = window.location;
	return hash.startsWith("#/")
		? (hash.slice(1).split("?")[0] ?? "/")
		: pathname;
}

/**
 * Record a usage event. Never throws; a no-op in dev builds unless
 * VITE_ANALYTICS_DEV=true. Send counts, ids and enums only: never prompt
 * text, board content, emails or URLs of user content.
 */
export function track(name: string, properties?: EventProperties): void {
	if (!enabled) {
		return;
	}
	try {
		queue.enqueue(name, properties, currentPath());
		if (queue.size() >= FLUSH_AT) {
			queue.flush().catch(() => {
				/* best-effort */
			});
			return;
		}
		scheduleFlush();
	} catch {
		// Never let analytics break the app.
	}
}

/** Send what's queued now (e.g. before signing out). Never rejects. */
export async function flushAnalytics(): Promise<void> {
	if (!enabled) {
		return;
	}
	if (flushTimer !== null) {
		clearTimeout(flushTimer);
		flushTimer = null;
	}
	try {
		await queue.flush();
	} catch {
		// best-effort
	}
}

/** Start tracking: call once at startup. Records `app.opened` for new sessions. */
export function initAnalytics(): void {
	if (enabled || (import.meta.env.DEV && !DEV_ENABLED)) {
		return;
	}
	try {
		enabled = true;
		document.addEventListener("visibilitychange", () => {
			if (document.visibilityState === "hidden") {
				flushOnExit();
			}
		});
		window.addEventListener("pagehide", flushOnExit);
	} catch {
		// Never let analytics break the app.
	}
}
